import React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import CircularProgress from '@mui/material/CircularProgress'
import moment from 'moment'
import {
  dropSelfCollisions,
  formatCollision,
  formatReservationTime,
} from '../utils/activityReservations'

/**
 * Shown after saving when the activity's time moved away from its attached court
 * bookings. Re-checks conflicts at the new time before offering to move them, since
 * the new slot may already be taken by someone else.
 */
function ActivityReservationSyncDialog({ open, activity, reservations, firebase, onLeave, onMove, moving }) {
  const [conflicts, setConflicts] = React.useState([])
  const [checking, setChecking] = React.useState(false)
  const [override, setOverride] = React.useState(false)
  const [checkError, setCheckError] = React.useState(false)

  const rows = React.useMemo(() => reservations || [], [reservations])

  React.useEffect(() => {
    let cancelled = false
    const check = async () => {
      if (!open || rows.length === 0 || !activity) return
      setChecking(true)
      setOverride(false)
      setCheckError(false)
      const resources = rows.map((r) => ({ id: r.resourceid, displayName: r.resourcename }))
      const result = await firebase.checkResourceConflicts(activity.start, activity.end, resources)
      if (cancelled) return
      setChecking(false)
      if (!result || result.error) {
        setCheckError(true)
        setConflicts([])
        return
      }
      // A reservation being moved still occupies its OLD slot, and the live conflict
      // callable returns no reservation id, so self-collisions can only be spotted by
      // matching the old time range.
      const cleaned = result.map((row, index) =>
        dropSelfCollisions(row, [{ start: rows[index].start, end: rows[index].end }])
      )
      setConflicts(cleaned.filter((row) => row.isconflict))
    }
    check()
    return () => {
      cancelled = true
    }
  }, [open, rows, activity, firebase])

  if (!activity) return null

  const newTime = formatReservationTime({
    start: moment.utc(activity.start).format('YYYY-MM-DDTHH:mm:ssZ'),
    end: moment.utc(activity.end).format('YYYY-MM-DDTHH:mm:ssZ'),
  })

  const blocked = conflicts.length > 0 && !override

  return (
    <Dialog open={open} onClose={onLeave} maxWidth='sm' fullWidth>
      <DialogTitle>Aktiviteetin aika muuttui</DialogTitle>
      <DialogContent>
        <Typography variant='body1' gutterBottom>
          Uusi aika on {newTime}.
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          {rows.length === 1
            ? 'Yksi liitetty varaus ei enää vastaa aktiviteetin aikaa:'
            : rows.length + ' liitettyä varausta ei enää vastaa aktiviteetin aikaa:'}
        </Typography>
        {rows.map((reservation) => (
          <Typography key={reservation.id} variant='body2' sx={{ mb: 0.5 }}>
            <b>{reservation.resourcename || reservation.resourceid}</b> — {formatReservationTime(reservation)}
          </Typography>
        ))}

        {checking ? (
          <Typography variant='body2' sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={14} /> Tarkistetaan päällekkäisyydet uudessa ajassa...
          </Typography>
        ) : checkError ? (
          <Alert severity='warning' sx={{ mt: 2 }}>
            Päällekkäisyyksien tarkistus epäonnistui. Siirto on silti mahdollinen.
          </Alert>
        ) : conflicts.length > 0 ? (
          <Alert severity='warning' sx={{ mt: 2 }}>
            Uusi aika on jo varattu:
            {conflicts.map((row, index) => (
              <div key={index}>
                <b>{row.resource ? row.resource.displayName : ''}</b>
                {row.collision ? ' — ' + formatCollision(row.collision) : ''}
              </div>
            ))}
          </Alert>
        ) : null}

        {conflicts.length > 0 ? (
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox checked={override} onChange={(e) => setOverride(e.target.checked)} />}
            label='Siirrä silti'
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onLeave} disabled={moving}>
          Jätä ennalleen
        </Button>
        <Button onClick={onMove} variant='contained' color='primary' disabled={blocked || checking || moving}>
          {moving ? 'Siirretään...' : 'Siirrä varaukset'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ActivityReservationSyncDialog
